import {
    type EditorState,
    type Extension,
    RangeSetBuilder,
    StateEffect,
    StateField,
} from "@codemirror/state";
import {
    Decoration,
    type DecorationSet,
    EditorView,
    ViewPlugin,
    type ViewUpdate,
    WidgetType,
} from "@codemirror/view";
import type { IntelligenceInlayHint } from "@magic-md/engine-client/protocol";
import { createIntelligenceSessionResolver } from "./session";
import type { IntelligenceConfig } from "./types";

const INLAY_HINT_PREFETCH_LINES = 20;

class InlayHintWidget extends WidgetType {
    constructor(private readonly label: string) {
        super();
    }

    eq(other: InlayHintWidget): boolean {
        return other.label === this.label;
    }

    toDOM(): HTMLElement {
        const span = document.createElement("span");
        span.className = "cm-intelligence-inlay";
        span.textContent = this.label;
        return span;
    }

    ignoreEvent(): boolean {
        return true;
    }
}

export function buildInlayHintDecorations(
    state: EditorState,
    hints: IntelligenceInlayHint[],
): DecorationSet {
    if (hints.length === 0) return Decoration.none;

    // Hide hints on the cursor line to reduce clutter while editing
    const cursorLine = state.doc.lineAt(state.selection.main.head).number;

    // Resolve each hint to an absolute document position, filter invalid /
    // cursor-line hints, then sort by position. RangeSetBuilder requires
    // strictly non-decreasing `from` — the server may return hints in any
    // order, and `Math.min(..., line.to)` clamping can push multiple hints
    // to the same pos, both of which can violate the builder's rule and
    // throw "Ranges must be added sorted by `from` position and `startSide`".
    const resolved: { pos: number; label: string }[] = [];
    for (const hint of hints) {
        if (hint.kind === "ref-value") continue;
        if (hint.line < 1 || hint.line > state.doc.lines) continue;
        if (hint.line === cursorLine) continue;
        const line = state.doc.line(hint.line);
        const pos = Math.min(line.from + Math.max(0, hint.column), line.to);
        resolved.push({ pos, label: hint.label });
    }
    resolved.sort((a, b) => a.pos - b.pos);

    const builder = new RangeSetBuilder<Decoration>();
    for (const { pos, label } of resolved) {
        builder.add(
            pos,
            pos,
            Decoration.widget({
                widget: new InlayHintWidget(label),
                side: 1,
            }),
        );
    }

    return builder.finish();
}

const setHints = StateEffect.define<IntelligenceInlayHint[]>();

const inlayTheme = EditorView.baseTheme({
    ".cm-intelligence-inlay": {
        marginLeft: "8px",
        color: "var(--color-muted-foreground)",
        opacity: "0.72",
        fontSize: "11px",
        fontStyle: "italic",
        pointerEvents: "none",
        userSelect: "none",
        whiteSpace: "nowrap",
    },
});

export function getInlayHintRequestRange(
    view: Pick<EditorView, "visibleRanges" | "state">,
): { startLine: number; endLine: number } {
    const visibleRanges = Array.isArray(view.visibleRanges)
        ? view.visibleRanges
        : [{ from: 0, to: view.state.doc.length }];

    let startLine = view.state.doc.lines;
    let endLine = 1;

    for (const range of visibleRanges) {
        const fromLine = Math.max(
            1,
            view.state.doc.lineAt(range.from).number - INLAY_HINT_PREFETCH_LINES,
        );
        const toPos = Math.max(range.from, range.to - 1);
        const toLine = Math.min(
            view.state.doc.lines,
            view.state.doc.lineAt(toPos).number + INLAY_HINT_PREFETCH_LINES,
        );
        startLine = Math.min(startLine, fromLine);
        endLine = Math.max(endLine, toLine);
    }

    if (endLine < startLine) {
        return {
            startLine: 1,
            endLine: view.state.doc.lines,
        };
    }

    return { startLine, endLine };
}

export function intelligenceInlayHints(config: IntelligenceConfig): Extension {
    const resolveSession = createIntelligenceSessionResolver(config);
    const getSyncVersion = () => config.getSyncVersion?.() ?? 0;

    const rawField = StateField.define<IntelligenceInlayHint[]>({
        create: () => [],
        update(hints, tr) {
            for (const effect of tr.effects) {
                if (effect.is(setHints)) return effect.value;
            }
            return hints;
        },
    });

    const field = StateField.define<DecorationSet>({
        create: () => Decoration.none,
        update(decos, tr) {
            for (const effect of tr.effects) {
                if (effect.is(setHints)) {
                    return buildInlayHintDecorations(tr.state, effect.value);
                }
            }
            if (tr.docChanged) {
                return buildInlayHintDecorations(
                    tr.state,
                    tr.state.field(rawField),
                );
            }
            // Rebuild when cursor moves to a different line so hints on the
            // new cursor line are hidden and hints on the old line reappear.
            if (!tr.startState.selection.main.eq(tr.state.selection.main)) {
                const oldLine = tr.startState.doc.lineAt(
                    tr.startState.selection.main.head,
                ).number;
                const newLine = tr.state.doc.lineAt(
                    tr.state.selection.main.head,
                ).number;
                if (oldLine !== newLine) {
                    return buildInlayHintDecorations(
                        tr.state,
                        tr.state.field(rawField),
                    );
                }
            }
            return decos.map(tr.changes);
        },
        provide: (f) => EditorView.decorations.from(f),
    });

    const refresher = ViewPlugin.define((view) => {
        let pending: ReturnType<typeof setTimeout> | null = null;
        let requestVersion = 0;
        let lastAppliedKey: string | null = null;

        const clearHints = () => {
            lastAppliedKey = null;
            view.dispatch({ effects: setHints.of([]) });
        };

        const refresh = () => {
            const engine = config.getEngine();
            if (!engine?.intelligenceInlayHint) {
                clearHints();
                return;
            }

            const currentVersion = ++requestVersion;
            resolveSession()
                .then((session) => {
                    if (!session?.engine.intelligenceInlayHint) {
                        clearHints();
                        return null;
                    }

                    const range = getInlayHintRequestRange(view);
                    const requestKey = `${session.sessionId}:${getSyncVersion()}:${range.startLine}:${range.endLine}:${config.getLocale?.() ?? ""}`;
                    if (requestKey === lastAppliedKey) {
                        return null;
                    }
                    return session.engine.intelligenceInlayHint({
                        sessionId: session.sessionId,
                        startLine: range.startLine,
                        endLine: range.endLine,
                        locale: config.getLocale?.(),
                    }).then((result) => ({ requestKey, result }));
                })
                .then((result) => {
                    if (!result || currentVersion !== requestVersion) return;
                    lastAppliedKey = result.requestKey;
                    view.dispatch({ effects: setHints.of(result.result.hints ?? []) });
                })
                .catch(() => {
                    if (currentVersion !== requestVersion) return;
                    clearHints();
                });
        };

        const schedule = (delay: number) => {
            if (pending) clearTimeout(pending);
            pending = setTimeout(() => {
                pending = null;
                refresh();
            }, delay);
        };

        const unsubscribe = config.subscribeSessionId?.(() => {
            requestVersion += 1;
            clearHints();
            schedule(0);
        });
        const unsubscribeSyncVersion = config.subscribeSyncVersion?.(() => {
            requestVersion += 1;
            schedule(0);
        });

        if (config.getSessionId()) {
            schedule(0);
        }

        // No initial timer: subscribeSessionId fires when session becomes
        // available, which schedules the first refresh with delay 0.

        return {
            update(update: ViewUpdate) {
                if (update.viewportChanged) {
                    schedule(50);
                }
            },
            destroy() {
                if (pending) clearTimeout(pending);
                unsubscribe?.();
                unsubscribeSyncVersion?.();
            },
        };
    });

    return [rawField, field, refresher, inlayTheme];
}
